/**
 * Per-product branding module — single source of truth.
 *
 * Resolves at build time from REACT_APP_PRODUCT (set in deploy.yml matrix
 * leg). Both bundles use the same source code; only the assets + copy
 * differ between qutime.com and pureoa.com builds.
 *
 * Default to 'qutime' so local dev (`bun start`) without env vars still
 * renders something sensible.
 */
import qutimeLogoSrc from '../assets/qutime-logo.png';
import pureoaLogoSrc from '../assets/pureoa-logo.png';
import svdyLogoSrc from '../assets/svdy-logo.png';
import {
  PRODUCT_METADATA,
  resolveProductCode,
  type ProductCode,
  type ProductMetadata,
} from './metadata';

export type { ProductCode } from './metadata';

export const PRODUCT: ProductCode = resolveProductCode(process.env.REACT_APP_PRODUCT);

interface BrandingConfig extends ProductMetadata {
  /** PNG logo used in TopNav, OrgGate, Onboarding (transparent background). */
  logo: string;
  /** Onboarding hero illustration in /public/images/. */
  onboardingImage: string;
  /** i18n key for onboarding hero headline. */
  onboardingHeadlineKey: string;
  /** i18n key for onboarding hero subline. */
  onboardingSublineKey: string;
  /** Per-product favicon and description come from ProductMetadata. */
}

const BRANDING: Record<ProductCode, BrandingConfig> = {
  qutime: {
    ...PRODUCT_METADATA.qutime,
    logo: qutimeLogoSrc,
    onboardingImage: '/images/qutime-onboarding.png',
    onboardingHeadlineKey: 'common:branding.qutime.headline',
    onboardingSublineKey: 'common:branding.qutime.subline',
  },
  pureoa: {
    ...PRODUCT_METADATA.pureoa,
    logo: pureoaLogoSrc,
    onboardingImage: '/images/pureoa-onboarding.png',
    onboardingHeadlineKey: 'common:branding.pureoa.headline',
    onboardingSublineKey: 'common:branding.pureoa.subline',
  },
};

const active = BRANDING[PRODUCT];

export const PRODUCT_NAME = active.productName;
export const LOGO = active.logo;
/** Raw per-product logos — used to BLEND both for a bundle-licensed org
 *  (ADR-031 Phase 2b, temporary side-by-side until a dedicated bundle mark). */
export const QUTIME_LOGO = qutimeLogoSrc;
export const PUREOA_LOGO = pureoaLogoSrc;
/** The company mark. Used where a tenant is not identified by ONE product. */
export const SVDY_LOGO = svdyLogoSrc;
export const ONBOARDING_IMAGE = active.onboardingImage;
export const ONBOARDING_HEADLINE_KEY = active.onboardingHeadlineKey;
export const ONBOARDING_SUBLINE_KEY = active.onboardingSublineKey;
export const FAVICON = active.favicon;


/**
 * Which product mark a TENANT gets, from its licence rather than the build.
 *
 * WHY THIS IS HERE (2026-08-05)
 * `LOGO` and `PRODUCT_NAME` above resolve from REACT_APP_PRODUCT, which is fixed
 * per deployment. TopNav used `LOGO` for any org that was not a bundle, so a
 * PureOA-only org shown on the QuTime build got QuTime's mark — which a multi-org
 * admin hits the moment they switch into an org with the other licence.
 *
 * Exported as a function rather than inlined so it can be asserted. The first
 * version of that test re-implemented this logic in the test file, which meant
 * mutating the component left it green — verified by running the mutation.
 */
export type TenantMark = 'qutime' | 'pureoa' | 'bundle' | 'build-default';

export function tenantMarkFor(
  licensedProducts: readonly string[] | null | undefined,
): TenantMark {
  const hasQutime = !!licensedProducts?.includes('qutime');
  const hasPureoa = !!licensedProducts?.includes('pureoa');
  if (hasQutime && hasPureoa) return 'bundle';
  // No licence to read from: the build's own brand is the honest answer.
  if (!licensedProducts?.length) return 'build-default';
  return hasPureoa && !hasQutime ? 'pureoa' : 'qutime';
}

/** The logo src for a tenant mark. `build-default` is this deployment's brand. */
export function logoForMark(mark: TenantMark): string {
  if (mark === 'pureoa') return pureoaLogoSrc;
  if (mark === 'qutime') return qutimeLogoSrc;
  // A bundle tenant gets THE BRAND OF THE DOOR THEY CAME THROUGH — this build's
  // own mark — and deliberately not the company mark.
  //
  // The customer-facing domains are qutime.com and pureoa.com. A tenant reaches
  // the product through one of them, so putting "SVDY" in that header introduces a
  // third brand they have no relationship with: for an SMB admin that is the
  // question "who is SVDY?", which is friction rather than reassurance. The company
  // name belongs where money and law are — the invoice reads SVDY Inc., so does the
  // card statement and the privacy policy. Same principle as the invoice audit, and
  // the same principle Gmail follows by showing Gmail's mark rather than Alphabet's.
  //
  // It also settles what a pair could not: two wordmarks side by side cannot exceed
  // 20px tall in the 208px this rail leaves, against 28px for one, and the previous
  // `maxWidth: 90` clamp was silently rendering them at 18.6px and 19.8px beneath a
  // 22px divider taller than both.
  //
  // Single-licence tenants are the OTHER case and keep their own product's mark
  // above: showing a QuTime wordmark to an org with no QuTime licence would be
  // worse than any domain mismatch.
  return active.logo;
}
